/**
 * Master-config grouping — collapses the long flat list of category codes
 * into a small set of domain "groups" the user can pick from the sidebar.
 *
 * Why this exists: the sidebar Master panel used to render every category
 * (lead_source / deal_stage / task_type / currency / …) as its own row,
 * which scaled poorly as the catalog grew. The UI revamp groups them by
 * domain so the sidebar shows "Company / Contact / Deal / Lead / Task /
 * Finance / Products / Approval Configuration / Compliance / Other" master
 * groups, and the page itself exposes the per-category picker.
 *
 * Adding a new category: extend `GROUP_PREFIXES`. Anything that doesn't match
 * lands in `'other'`, which is fine for one-off catalogs that don't belong
 * to a named domain.
 *
 * Lives in `@clarix/services-master-config` so BOTH the CRM remote (the
 * master-config page) and the shell host (the Master rail panel) render the
 * exact same grouping/icons from a single home.
 */

import {
  Wallet,
  Package,
  ClipboardCheck,
  Layers,
  Target,
  Activity,
  AlertTriangle,
  TrendingUp,
  Tag,
  Trophy,
  XCircle,
  ClipboardList,
  ListTodo,
  Flag,
  CheckCircle2,
  Handshake,
  Contact,
  Building2,
  RefreshCw,
  Paperclip,
  CreditCard,
  DollarSign,
  Percent,
  Receipt,
  FolderOpen,
  MessageSquareWarning,
  Cog,
  FileText,
  BarChart3,
  LayoutGrid,
  ListTree,
  ArrowUpDown,
  UserMinus,
  Users,
  BadgeDollarSign,
  HandCoins,
  Type,
  FileSearch,
  FileCheck,
  ShieldAlert,
  ShieldCheck,
  Lock,
  EyeOff,
  Fingerprint,
} from 'lucide-react';
import type { ElementType } from 'react';

export type MasterGroupId =
  | 'company'
  | 'contact'
  | 'deal'
  | 'lead'
  | 'task'
  | 'finance'
  | 'products'
  | 'approval'
  | 'compliance'
  | 'other';

export interface MasterGroup {
  id: MasterGroupId;
  label: string;
  /** Distinct Lucide icon so each sub-module in the Master panel reads as its own thing
   *  (Settings-panel style) instead of every row sharing the same Layers glyph. */
  icon: ElementType;
  /** Prefixes (matched against the category code) that belong to this group. */
  prefixes: string[];
}

/**
 * Display order = sidebar order. The old catch-all `crm` group was split into
 * one group per CRM entity (Company / Contact / Deal / Lead / Task) so the
 * panel reads as a list of domains rather than one long CRM bucket. `dsr_*` and
 * `breach_*` were lifted out of `other` into their own Compliance group.
 *
 * NOTE: `attachment_category` no longer has a named home and lands in `other`.
 *
 * The DPDP field-classification catalogs (`dpdp_data_class`, `dpdp_masking_rule`,
 * `dpdp_identity_role`) also belong to the Compliance/DSR group alongside `dsr_`
 * and `breach_`.
 */
export const MASTER_GROUPS: MasterGroup[] = [
  { id: 'company',    label: 'Company Master',    icon: Building2,      prefixes: ['company_'] },
  { id: 'contact',    label: 'Contact Master',    icon: Contact,        prefixes: ['contact_'] },
  { id: 'deal',       label: 'Deal Master',       icon: Handshake,      prefixes: ['deal_'] },
  { id: 'lead',       label: 'Lead Master',       icon: Target,         prefixes: ['lead_'] },
  { id: 'task',       label: 'Task Master',       icon: ListTodo,       prefixes: ['task_'] },
  { id: 'finance',    label: 'Finance Master',    icon: Wallet,         prefixes: ['billing_', 'currency', 'margin_', 'invoice_', 'subscription_'] },
  { id: 'products',   label: 'Products Master',   icon: Package,        prefixes: ['product_'] },
  { id: 'approval',   label: 'Approval Configuration', icon: ClipboardCheck, prefixes: ['approval_'] },
  { id: 'compliance', label: 'Compliance Master', icon: ShieldCheck,    prefixes: ['dsr_', 'breach_', 'dpdp_'] },
  { id: 'other',      label: 'Other Master',      icon: Layers,         prefixes: [] },
];

/** Resolve which group a category belongs to. Defaults to `'other'`. */
export function groupForCategoryCode(code: string): MasterGroupId {
  const lower = code.toLowerCase();
  for (const group of MASTER_GROUPS) {
    if (group.id === 'other') continue;
    if (group.prefixes.some((prefix) => lower === prefix || lower.startsWith(prefix))) {
      return group.id;
    }
  }
  return 'other';
}

/**
 * Is `value` a live group id? Guards `?group=` deep links — the group ids
 * changed when `crm` was split, so a bookmarked `?group=crm` must fall back to
 * the default rather than resolving to an empty category list.
 */
export function isMasterGroupId(value: string): value is MasterGroupId {
  return MASTER_GROUPS.some((g) => g.id === value);
}

/**
 * Icon lookup for individual master categories shown as sub-modules in the
 * sidebar. Keyed by the exact backend `code`; falls back to the owning group's
 * icon when the category isn't explicitly mapped (e.g. newly seeded codes).
 */
export const CATEGORY_ICONS: Record<string, ElementType> = {
  // Company / Contact
  company_industry:             Building2,
  company_lifecycle:            RefreshCw,
  contact_role:                 Contact,
  // Deal
  deal_stage:                   TrendingUp,
  deal_type:                    Tag,
  deal_won_reason:              Trophy,
  deal_lost_reason:             XCircle,
  // Lead
  lead_source:                  Target,
  lead_status:                  Activity,
  lead_disqualification_reason: AlertTriangle,
  // Task
  task_type:                    ClipboardList,
  task_priority:                Flag,
  task_status:                  CheckCircle2,
  // Unrouted
  attachment_category:          Paperclip,
  // Finance
  billing_model:        CreditCard,
  currency:             DollarSign,
  margin_status:        Percent,
  invoice_status:       Receipt,
  invoice_document_type: FileText,
  subscription_status:  RefreshCw,
  // Products
  product_type:     Package,
  product_category: FolderOpen,
  // Approval
  approval_reason: MessageSquareWarning,
  approval_action: ClipboardCheck,
  // Compliance
  dsr_request_type:     FileSearch,
  dsr_status:           FileCheck,
  breach_incident_type: ShieldAlert,
  dpdp_data_class:      Lock,
  dpdp_masking_rule:    EyeOff,
  dpdp_identity_role:   Fingerprint,
  // Other (no shared prefix → land in `'other'` bucket). One distinct Lucide
  // glyph per category so the Other section reads with visual hierarchy
  // instead of every row sharing the group fallback icon.
  system_config:        Cog,
  forecast_category:    BarChart3,
  view_layout:          LayoutGrid,
  view_group:           ListTree,
  view_sort:            ArrowUpDown,
  churn_reason:         UserMinus,
  team_scope:           Users,
  commission_status:    BadgeDollarSign,
  payout_status:        HandCoins,
  custom_field_type:    Type,
};

/** Returns the Lucide icon for a master category, falling back to its group icon. */
export function iconForCategoryCode(code: string): ElementType {
  const direct = CATEGORY_ICONS[code.toLowerCase()];
  if (direct) return direct;
  const group = MASTER_GROUPS.find((g) => g.id === groupForCategoryCode(code));
  return group?.icon ?? Layers;
}
